import { useContext, useEffect, useState } from 'react'
import { UIContext } from 'Context/UI'

import { useCallbackPrompt } from 'Hooks/useCallbackPrompt'
import { useNavigate } from 'react-router-dom'
import { useProfileStore } from 'Store/Profile'
import { useUserStore } from 'Store/User'
import { shallow } from 'zustand/shallow'
import { toast } from 'react-toastify'

import { TABS } from '../Config'

const VIEW_INDEX = 4

const ViewModel = () => {
	const { setShowContentToolbar, setCustomContentToolbar } = useContext(UIContext)
	const navigate = useNavigate()
	const companyProfile = useProfileStore(state => state.profile, shallow)
	const profile = useProfileStore(state => state.userProfile, shallow)
	const getUserProfile = useProfileStore(state => state.getUserProfile, shallow)
	const { updateUser, changePasswordUser } = useUserStore(
		state => ({
			updateUser: state.edit,
			changePasswordUser: state.changePassword,
		}),
		shallow
	)
	const [formState, setFormState] = useState({
		id: profile?.id ?? '',
		email: '',
		oldPassword: '',
		newPassword: '',
		confirmPassword: '',
	})
	const [hasChanged, setHasChanged] = useState(false)
	const [showOldPassword, setShowOldPassword] = useState<boolean>(false)
	const [showNewPassword, setShowNewPassword] = useState<boolean>(false)
	const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false)

	useCallbackPrompt(hasChanged)

	const onChangeShowPassword = (name: string) => {
		if (name === 'oldPassword') {
			setShowOldPassword(prev => !prev)
		} else if (name === 'newPassword') {
			setShowNewPassword(prev => !prev)
		} else if (name === 'confirmPassword') {
			setShowConfirmPassword(prev => !prev)
		}
	}

	const findTabPath = (tabIndex: number) => {
		const tab = TABS.find((_, idx) => idx === tabIndex)
		if (tab) {
			return tab.path
		}
	}

	const onChangeTab = (tabIndex: number) => {
		if (tabIndex !== VIEW_INDEX) {
			navigate(findTabPath(tabIndex) ?? '#')
		}
	}

	const onChange = (key: string, value: string) => {
		setFormState(prev => ({ ...prev, [key]: value }))
		setHasChanged(true)
	}

	const onSubmitEmail = async () => {
		if (hasChanged) {
			const { email } = formState
			const expression: RegExp = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i

			if (email.length === 0) {
				toast.error('Please enter your email')
				return
			} else if (expression.test(email) === false) {
				toast.error('Please enter a valid email')
				return
			} else {
				const { success, data } = await updateUser(formState.id, { email })
				if (!success) {
					toast.error(data)
					return
				} else {
					toast.success('บันทึกอีเมลใหม่สำเร็จ')
					setHasChanged(false)
					const { data: pData } = await getUserProfile()
					setFormState(prev => ({ ...prev, email: pData?.email ?? '' }))
				}
			}
		}
	}

	const onSubmitPassword = async () => {
		if (
			hasChanged &&
			(formState.oldPassword.length > 0 ||
				formState.newPassword.length > 0 ||
				formState.confirmPassword.length > 0)
		) {
			const { oldPassword, newPassword, confirmPassword } = formState

			if (oldPassword.length === 0) {
				toast.error('Please enter your old password')
				return
			} else if (newPassword.length === 0) {
				toast.error('Please enter your new password')
				return
			} else if (confirmPassword.length === 0) {
				toast.error('Please enter your confirm password')
				return
			} else if (newPassword !== confirmPassword) {
				toast.error('New password and confirm password not match')
				return
			} else {
				const { success, data } = await changePasswordUser(formState.id, {
					oldPassword,
					password: newPassword,
				})
				if (!success) {
					toast.error(data)
					return
				} else {
					toast.success('เปลี่ยนรหัสผ่านสำเร็จ')
					setHasChanged(false)
					setFormState(prev => ({ ...prev, oldPassword: '', newPassword: '', confirmPassword: '' }))
				}
			}
		}
	}

	useEffect(() => {
		setShowContentToolbar(false)
		setCustomContentToolbar(null)

		return () => {
			setShowContentToolbar(true)
			setCustomContentToolbar(null)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		setFormState({
			id: profile?.id ?? '',
			email: profile?.email ?? '',
			oldPassword: '',
			newPassword: '',
			confirmPassword: '',
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	return {
		companyName: companyProfile?.companyName,
		currentTab: VIEW_INDEX,
		onChangeTab,
		formState,
		onChange,
		onSubmitEmail,
		onSubmitPassword,
		// Show Password
		showOldPassword,
		showNewPassword,
		showConfirmPassword,
		onChangeShowPassword,
	}
}

export default ViewModel
